import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const CART_KEY = "td_cart";

export interface CartItem {
  id: number;
  tubeName: string;
  brand: string | null;
  askingPrice: string;
  askingPriceBasis?: "per_unit" | "whole_lot";
  lotTubeCount?: number;
  sellerUsername: string;
  sellerDisplayName: string;
  qty: number;
}

export interface SellerGroup {
  sellerUsername: string;
  sellerDisplayName: string;
  items: CartItem[];
  subtotal: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, "qty">) => void;
  removeItem: (id: number) => void;
  updateQty: (id: number, qty: number) => void;
  clearCart: () => void;
  itemCount: number;
  bySeller: SellerGroup[];
}

const CartContext = createContext<CartContextType | null>(null);

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch { /* quota exceeded — ignore */ }
}

function groupBySeller(items: CartItem[]): SellerGroup[] {
  const map = new Map<string, SellerGroup>();
  for (const item of items) {
    if (!map.has(item.sellerUsername)) {
      map.set(item.sellerUsername, {
        sellerUsername:    item.sellerUsername,
        sellerDisplayName: item.sellerDisplayName,
        items:             [],
        subtotal:          0,
      });
    }
    const g = map.get(item.sellerUsername)!;
    g.items.push(item);
    g.subtotal += parseFloat(item.askingPrice) * item.qty;
  }
  return Array.from(map.values());
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => { saveCart(items); }, [items]);

  const addItem = useCallback((item: Omit<CartItem, "qty">) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (item.askingPriceBasis === "whole_lot") {
        if (existing) return prev.map(i => i.id === item.id ? { ...item, qty: 1 } : i);
        return [...prev, { ...item, qty: 1 }];
      }
      if (existing) {
        return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...item, qty: 1 }];
    });
  }, []);

  const removeItem = useCallback((id: number) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const updateQty = useCallback((id: number, qty: number) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => i.id !== id));
    } else {
      setItems(prev => prev.map(i => i.id === id
        ? { ...i, qty: i.askingPriceBasis === "whole_lot" ? 1 : qty }
        : i));
    }
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const bySeller  = groupBySeller(items);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clearCart, itemCount, bySeller }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
